import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import { CalendarMonthEventPill } from './calendar-event-block'
import CalendarBookSlotIcon from './calendar-book-slot-icon'
import {
  getBookingEventKey,
  getMonthGridDays,
  isCurrentMonth,
  isPastBookSlot,
  isToday,
  MONTH_VISIBLE_EVENTS,
} from './calendar-utils'
import useCalendarStyles from './calendar-styles'

const WEEKDAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const CalendarMonthView = ({
  anchorDate,
  bookingsByDate,
  canBookStudent,
  bookingUrl,
  onDayClick,
  onBookSlotClick,
  onReschedule,
  onPayment,
  onCancellation,
}) => {
  const classes = useCalendarStyles()
  const days = getMonthGridDays(anchorDate)

  return (
    <>
      <Box className={classes.calendarHeader}>
        <Typography className={classes.calendarTitle}>
          {anchorDate.toFormat('LLLL yyyy')}
        </Typography>
      </Box>

      <Box className={classes.monthGrid}>
        {WEEKDAY_HEADERS.map(day => (
          <Typography key={day} className={classes.monthWeekdayHeader}>
            {day}
          </Typography>
        ))}

        {days.map(day => {
          const dateKey = day.toISODate()
          const dayBookings = bookingsByDate[dateKey] || []
          const visibleBookings = dayBookings.slice(0, MONTH_VISIBLE_EVENTS)
          const hiddenCount = dayBookings.length - visibleBookings.length
          const dayIsToday = isToday(day)
          const inCurrentMonth = isCurrentMonth(day, anchorDate)
          const isBookable =
            canBookStudent && bookingUrl && !isPastBookSlot(day)
          const dayCellClassName = [
            classes.monthDayCell,
            !inCurrentMonth ? classes.monthDayOutside : '',
            dayIsToday ? classes.monthDayToday : '',
            isBookable ? classes.monthDayCellBookable : '',
          ]
            .filter(Boolean)
            .join(' ')

          const handleDayClick = () => {
            onDayClick?.(day)
          }

          return (
            <Box key={dateKey} className={dayCellClassName}>
              {onDayClick ? (
                <button
                  type="button"
                  className={classes.monthDayNumber}
                  onClick={handleDayClick}
                  aria-label={`View ${day.toFormat('cccc, LLLL d')}`}
                >
                  {day.day}
                </button>
              ) : (
                <span className={classes.monthDayNumber}>{day.day}</span>
              )}
              {visibleBookings.map(booking => (
                <Box
                  key={getBookingEventKey(booking)}
                  onClick={event => event.stopPropagation()}
                  onKeyDown={event => event.stopPropagation()}
                >
                  <CalendarMonthEventPill
                    booking={booking}
                    onReschedule={onReschedule}
                    onPayment={onPayment}
                    onCancellation={onCancellation}
                  />
                </Box>
              ))}
              {hiddenCount > 0 ? (
                onDayClick ? (
                  <button
                    type="button"
                    className={classes.monthMoreButton}
                    onClick={handleDayClick}
                  >
                    +{hiddenCount} more
                  </button>
                ) : (
                  <Typography
                    component="span"
                    className={classes.monthMoreButton}
                  >
                    +{hiddenCount} more
                  </Typography>
                )
              ) : null}
              {isBookable ? (
                <Box
                  component="button"
                  type="button"
                  className={classes.monthBookSlotTarget}
                  onClick={event => {
                    event.stopPropagation()
                    onBookSlotClick?.(event, { day, hour: null })
                  }}
                  aria-label={`Add to calendar on ${day.toFormat(
                    'cccc, LLLL d',
                  )}`}
                >
                  <CalendarBookSlotIcon className={classes.monthBookSlotIcon} />
                </Box>
              ) : null}
            </Box>
          )
        })}
      </Box>
    </>
  )
}

CalendarMonthView.propTypes = {
  anchorDate: PropTypes.object.isRequired,
  bookingsByDate: PropTypes.object.isRequired,
  canBookStudent: PropTypes.bool,
  bookingUrl: PropTypes.string,
  onDayClick: PropTypes.func,
  onBookSlotClick: PropTypes.func,
  onReschedule: PropTypes.func,
  onPayment: PropTypes.func,
  onCancellation: PropTypes.func,
}

export default CalendarMonthView
